import { defaultStepsAmount } from '../../helpers/worksheetDefaults';
import InventoryPriceReference, { priceText } from './InventoryPriceReference';
import CalculationWorksheetSidebar from '../pipeline/CalculationWorksheetSidebar';
import { homeClassification, homeTypeLabel } from '../../helpers/homeClassification';
import React, { useEffect, useState, useRef } from 'react';
import { Button, Card, Col, Container, Modal, ProgressBar, Row, Table } from 'react-bootstrap';
import { ArrowDownCircle, ArrowUpCircle, Files, Pen, Trash } from 'react-bootstrap-icons';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import FormGenerator from '../../form-generator-v2';
import { deleteFileFromServer, makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { addDocHubFormFieldsToForm, getObjectForPrefill, getModelOptions } from '../../helpers/formHelpers';
import AlertModal from '../AlertModal';
import Comments from '../comments/Comments';
import File from '../common/file';
import Image from '../common/Image';
import FileViewer from '../common/FileViewer';
import Loader from '../Loader';
import NotFound from '../NotFound';
import SubmitButtonRow from '../common/SubmitButtonRow';
import { addChoicesToInventoryForm, editFormJson, validateAndCleanupForm } from './form';
import { updateSizesAccordingToSections } from '../../helpers/global';
import { applyCommissionValuesToFormValues, createCommissionResetHandlers } from './inventoryCommissionHelpers';
import { AddEditModal, getForm, prepareModelFormForApi, validateModelForm } from '../admin/ManufacturerModels';
import { modelAddRows, modelUpdateRows } from '../admin/constants';
import {
  SKIRTING_TYPE_OPTIONS,
  getSkirtingAllowanceByType,
  getAllowanceByItemName
} from '../pipeline/CalculationWorksheet';
import {
  INVENTORY_DELETION_ROWS,
  buildInventoryFormValuesWithWorksheetDefaults,
  buildWorksheetPayloadFromForm
} from './inventoryWorksheetHelpers';
import InventoryQuickField from './InventoryQuickField';
import { isSuperAdmin } from '../../helpers/roleHelpers';

const EditInventory = ({
  inventoryFromSearch,
  inventoryId: inventoryIdProp,
  appChoices,
  fromSearch,
  onInventoryEdit,
  onInventoryQuickEdit,
  onInventoryDelete,
  deletable = true,
  refetchChoices
}) => {
  const { id: routeId } = useParams();
  const formRef = useRef();
  const [inventoryId] = useState(inventoryFromSearch?._id || inventoryIdProp || routeId);
  const [form, setForm] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [models, setModels] = useState([]);

  const [isFormLoading, setFormLoading] = useState(!fromSearch);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [inventoryError, setInventoryError] = useState('');
  const [formDisabled, setFormDisabled] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [inventoryDeleting, setInventoryDeleting] = useState(false);
  const [showWorksheet, setShowWorksheet] = useState(false);
  const [inventory, setInventory] = useState();
  const [commentText, setCommentText] = useState('');
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [images, setImages] = useState([]);
  const [deleteFileModalMeta, setDeleteFileModalMeta] = useState(null);
  const [deleteCommentModalMeta, setDeleteCommentModalMeta] = useState(null);
  const [modelModalMeta, setModelMetaState] = useState({
    show: false,
    loading: false,
    defaultValues: null,
    disclaimer: ''
  });
  const [isCalculationWorkSheetVisible, setIsCalculationWorkSheetVisible] = useState(false);

  const onManufacturerChange = (manufacturerId, clearModel = true) => {
    const allModels = (appChoices.find(a => a.key === 'model')?.values || []).filter(Boolean);
    const filteredModels = allModels.filter(m => {
      const man = m.manufacturer;
      const manId = typeof man === 'object' ? man?._id : man;
      return manId === manufacturerId;
    });

    setModels(filteredModels);

    // Auto-select the first model if available and clearModel is true
    if (clearModel && filteredModels.length > 0) {
      const firstModel = filteredModels[0];
      setFormValues(prev => ({
        ...prev,
        model: firstModel._id
      }));

      // Trigger model change to update related fields
      onModelChange(firstModel._id);
    } else if (clearModel) {
      setFormValues(prev => ({
        ...prev,
        model: ''
      }));
    }
  };

  const onModelChange = modelId => {
    const allModels = (appChoices.find(a => a.key === 'model')?.values || []).filter(Boolean);
    const model = allModels.find(m => m._id === modelId);
    if (!model) return;

    setFormValues(prev => {
      const next = { ...prev };
      if (model.boxSize) {
        next.sizeOverall = model.boxSize;
      }
      if (model.bedRooms !== undefined) next.bedRooms = model.bedRooms;
      if (model.bathRooms !== undefined) next.bathRooms = model.bathRooms;
      if (model.livingRooms != null) next['livingRooms-additionalDochubField'] = model.livingRooms;
      if (model.numberOfSections !== undefined) next.numberOfSections = model.numberOfSections;

      // Update sizes based on sizeOverall and numberOfSections (use model values if available, otherwise existing form values)
      const sizeOverallToUse = next.sizeOverall || prev.sizeOverall;
      const numberOfSectionsToUse = next.numberOfSections !== undefined ? next.numberOfSections : prev.numberOfSections;
      if (sizeOverallToUse) {
        Object.assign(next, updateSizesAccordingToSections(sizeOverallToUse, Number(numberOfSectionsToUse || 1)));
      }

      return next;
    });
  };

  const history = useHistory();

  const fetchUnit = async () => {
    if (!inventoryId) {
      setFormLoading(false);
      return setInventoryError('Inventory unit not found!');
    }

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_SEARCH_NEW,
      requestBody: {
        filter: {
          _id: inventoryId
        },
        populate: true,
        includeContract: false,
        includeContact: false,
        returnOnlyInventoryInStock: false,
        pageSize: 1
      }
    });

    setFormLoading(false);
    if (error) {
      return setInventoryError(error);
    }

    const results = Array.isArray(response) ? response : response?.results || [];
    if (results.length === 0) {
      return setInventoryError('Inventory unit not found!');
    }

    updateInventoryState(results[0]);
  };

  useEffect(() => {
    if (!inventoryFromSearch) {
      fetchUnit();
    } else {
      updateInventoryState(inventoryFromSearch);
    }
  }, []);

  useEffect(() => {
    setUpForm();
  }, [formValues, models]);

  useEffect(() => {
    if (formValues.manufacturer && formValues.manufacturer !== 'dropdown-placeholder' && models.length === 0) {
      onManufacturerChange(formValues.manufacturer, false); // Don't clear the model on initial load
    }
  }, [formValues.manufacturer]);

  useEffect(() => {
    if (inventory) {
      const existingPhotos = (inventory.photos || []).map((photo, index) => ({
        ...photo,
        id: `existing-${index}`,
        filename: photo.url,
        isExisting: true
      }));
      setImages(existingPhotos);
    }
  }, [inventory]);

  useEffect(() => {
    if (!formDisabled) {
      if (inventory) {
        const existingPhotos = (inventory.photos || []).map((photo, index) => ({
          ...photo,
          id: `existing-${index}`,
          filename: photo.url,
          isExisting: true
        }));
        setImages(existingPhotos);
      }
    }
  }, [formDisabled, inventory]);

  const updateSizes = (sizeOverall, numberOfSections) => {
    const sizes = updateSizesAccordingToSections(sizeOverall, Number(numberOfSections));
    setFormValues({ ...formValues, sizeOverall, numberOfSections, ...sizes });
  };

  const onNumberOfSectionsChange = numberOfSections => {
    const nextNumberOfSections = Number(numberOfSections);
    setFormValues(prev => {
      const sizes = updateSizesAccordingToSections(prev.sizeOverall, nextNumberOfSections);

      return {
        ...prev,
        numberOfSections: nextNumberOfSections,
        ...sizes
      };
    });
  };

  const onUnitLocationChange = unitLocation => {
    setFormValues(prev => ({ ...prev, unitLocation }));
  };

  const onSizeOverallChange = sizeOverall => {
    updateSizes(sizeOverall, formValues.numberOfSections);
  };

  const handleFormValuesChange = newValues => {
    setFormValues(newValues);
  };

  const setUpForm = () => {
    const form = editFormJson({
      fromPipeline: true,
      numberOfSections: Number(formValues.numberOfSections),
      onNumberOfSectionsChange,
      onSizeOverallChange,
      onManufacturerChange,
      onModelChange,
      onCreateModelClick,
      onUnitLocationChange,
      models
    });
    const newFormJson = addDocHubFormFieldsToForm({ formJson: form, appChoices, model: 'Inventory' });

    addChoicesToInventoryForm({
      appChoices,
      form: newFormJson,
      unitLocationId: formValues.unitLocation,
      numberOfSections: formValues.numberOfSections,
      commissionResetHandlers: createCommissionResetHandlers(appChoices, setFormValues)
    });

    // Convert to new form generator format (single form object instead of forms array)
    const formConfig = newFormJson.forms[0];

    setForm(formConfig);
  };

  const handleWorksheetFieldChange = key => event => {
    const { value } = event.target;
    setFormValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const updateInventoryState = inventory => {
    setInventory(inventory);
    setFormLoading(false);
    setFormValues(prev => {
      const baseValues = getObjectForPrefill(inventory, true);
      const mappedWorksheet = buildInventoryFormValuesWithWorksheetDefaults(inventory);
      const { options, values } = getModelOptions(appChoices, baseValues.manufacturer, inventory?.model);

      return applyCommissionValuesToFormValues({
        ...baseValues,
        ...mappedWorksheet,
        ...(options.length ? { modelOptions: options, modelOptionValues: values } : {})
      });
    });
  };

  const saveQuickUnitField = async (field, value) => {
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventoryId) + '/quick-edit', method: 'PUT',
      requestBody: { field, value, expectedUpdatedAt: inventory.updatedAt }
    });
    if (error) throw new Error(error);
    const saved = { ...inventory, updatedAt: response.updatedAt };
    if (field === 'livingRooms') saved.additionalFields = { ...inventory.additionalFields, livingRooms: response.value };
    else saved[field] = response.value;
    if (field === 'numberOfSections') {
      const classification = homeClassification(saved);
      saved.effectiveNumberOfSections = classification.sections;
      saved.homeTypeNeedsReview = classification.needsReview;
      saved.homeTypeBasis = classification.basis;
    }
    updateInventoryState(saved);
    if (onInventoryQuickEdit) onInventoryQuickEdit(saved);
    toast.success('Inventory updated');
  };

  const onCreateModelClick = newValue => {
    const manufacturerChoice = appChoices.find(a => a.key === 'manufacturer');
    const manufacturer = manufacturerChoice?.values.find(m => m._id === formValues.manufacturer);
    const manufacturerName = manufacturer?.factoryShortName || manufacturer?.name || 'this manufacturer';

    setModelMetaState({
      show: true,
      loading: false,
      defaultValues: {
        modelName: newValue,
        manufacturer: formValues.manufacturer,
        bathRooms: formValues.bathRooms,
        bedRooms: formValues.bedRooms,
        livingRooms: formValues['livingRooms-additionalDochubField'],
        boxSize: formValues.sizeOverall,
        numberOfSections: formValues.numberOfSections
      },
      disclaimer: `This Model will be added to the ${manufacturerName} Manufacturer`
    });
  };

  const handleAddModelSubmit = async form => {
    setModelMetaState(prev => ({ ...prev, loading: true }));

    if (!validateModelForm(form)) {
      setModelMetaState(prev => ({ ...prev, loading: false }));
      return false;
    }

    const { photos, cleanForm } = prepareModelFormForApi(form);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.MODELS_BASE,
      requestBody: {
        ...cleanForm,
        photos,
        manufacturer: modelModalMeta.defaultValues?.manufacturer || formValues.manufacturer
      }
    });

    if (error) {
      toast.error(error);
      setModelMetaState(prev => ({ ...prev, loading: false }));
      return false;
    }

    if (response) {
      if (response.livingRooms != null) setFormValues(prev => ({ ...prev, 'livingRooms-additionalDochubField': response.livingRooms }));
      if (refetchChoices) {
        await refetchChoices();

        // optimistic update after refetch appChoices
        setModels(prev => [response, ...prev.filter(model => model._id !== response._id)]);
        setFormValues(prev => ({ ...prev, model: response._id }));
        onModelChange(response._id);
      }
    }

    setModelMetaState({
      show: false,
      loading: false,
      defaultValues: null,
      disclaimer: ''
    });
    toast.success(response?.reusedExisting ? 'Existing model selected. Its saved details were preserved.' : 'Model created successfully!');

    return true;
  };

  // Set up window function (required by old FormGenerator)
  window['onNewModelFormSubmit'] = handleAddModelSubmit;

  const handleSubmitClick = async () => {
    if (formRef.current) {
      await formRef.current.submitForm();
    }
  };

  const onInventoryEditFormSubmit = async formData => {
    if (uploadedFiles.some(f => f.status === 'ERROR') || images.some(f => f.status === 'ERROR')) {
      return toast.error('One or more files have failed uploading, please discard these files and try again!');
    }

    if (
      uploadedFiles.some(f => f.status !== 'UPLOADED') ||
      images.some(f => f.status !== 'UPLOADED' && !f.isExisting)
    ) {
      return toast.error('Some of the files are still uploading to the server, please try again in a moment!');
    }

    //checks
    const valid = validateAndCleanupForm(formData, inventory?.additionalFields, inventory?.customEntities);
    if (!valid) return;

    // Shape flat worksheet fields into nested payload expected by backend
    Object.assign(formData, buildWorksheetPayloadFromForm(formData));

    formData['files'] = [
      ...(inventory?.files || []).map(f => (typeof f === 'object' && f?._id ? f._id : f)).filter(Boolean),
      ...uploadedFiles.map(f => f.id)
    ];
    formData['photos'] = images
      .filter(img => img.filename)
      .map(img => ({
        url: img.filename,
        uploadedAt: img.uploadedAt || new Date()
      }));

    toast.info('Please wait, updating inventory item...');
    setButtonsDisabled(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventoryId),
      requestBody: { ...formData, comments: undefined },
      method: 'PUT'
    });

    setButtonsDisabled(false);

    if (error) {
      toast.error('Unable to update inventory item!');
    } else if (response) {
      toast.success('Inventory item updated successfully!');
      setUploadedFiles([]);
      updateInventoryState(response);
      setFormDisabled(true);
      if (onInventoryEdit) onInventoryEdit(response);
      if (response.photos) {
        const existingPhotos = response.photos.map((photo, index) => ({
          ...photo,
          id: `existing-${index}`,
          filename: photo.url,
          isExisting: true
        }));
        setImages(existingPhotos);
      }
    }
  };

  const handleClose = () => {
    setShowDeleteModal(false);
  };

  const deleteInventory = async () => {
    setInventoryDeleting(true);

    const { response, error } = await makeApiRequests({
      method: 'DELETE',
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventoryId)
    });

    setInventoryDeleting(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast(`Inventory deleted successfully!`, {
      type: 'success'
    });

    if (fromSearch) {
      setShowDeleteModal(false);
      onInventoryDelete && onInventoryDelete(inventoryId);
    } else {
      history.push('/pipeline/inventory');
    }
  };

  const DeleteModal = () => {
    return (
      <Modal show={showDeleteModal} onHide={handleClose} centered backdrop="static">
        <Modal.Header>
          <Modal.Title>Delete Inventory</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {inventory && inventory['contact'] && inventory['contact'].length > 0 && (
            <>
              This inventory is a potential unit for buyers{' '}
              <b>{inventory['contact'].map(c => c['Buyer']).join(', ')}. </b>
              Deleting it will remove it as a potential unit from all the buyers.{' '}
            </>
          )}
          {inventory && inventory['contract'] && (
            <>
              This inventory has already been contracted with the buyer <b>{inventory['contract']['Buyer']}. </b>
              Deleting it will remove it from the contracted buyer.{' '}
            </>
          )}
          Are you sure you want to delete this inventory? This action cannot be undone!
          {inventoryDeleting && <ProgressBar animated now={100} label="Deleting inventory..." />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={inventoryDeleting}>
            No
          </Button>
          <Button variant="danger" onClick={deleteInventory} disabled={inventoryDeleting}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  if (inventoryError) {
    return <NotFound text={inventoryError} />;
  }

  const submitComment = async () => {
    toast.info('Adding comment...');
    setButtonsDisabled(true);

    const { response, error } = await makeApiRequests({
      requestBody: {
        comments: commentText
      },
      method: 'PUT',
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventory._id)
    });

    setButtonsDisabled(false);

    if (error) {
      return toast.error(error);
    }

    toast.success(`Comment added successfully!`);
    setCommentText('');
    updateInventoryState(response);
    onInventoryEdit && onInventoryEdit(response);
  };

  const onCommentDeleteClick = comment => {
    setDeleteCommentModalMeta({ comment });
  };

  const deleteComment = async () => {
    const commentToDelete = deleteCommentModalMeta.comment._id;
    setDeleteCommentModalMeta({ ...deleteCommentModalMeta, showProgress: true });

    const { response, error } = await makeApiRequests({
      requestBody: {
        commentId: commentToDelete
      },
      method: 'POST',
      endpoint: ENDPOINTS.INVENTORY_DELETE_COMMENT(inventory._id)
    });

    setDeleteCommentModalMeta({ ...deleteCommentModalMeta, showProgress: false });

    if (error) {
      return toast.error(error);
    }

    setDeleteCommentModalMeta(null);
    toast.success(`Comment deleted successfully!`);
    updateInventoryState(response);
    onInventoryEdit && onInventoryEdit(response);
  };

  const onImagesReorder = newImages => {
    setImages(newImages);
  };

  const onImageDelete = async (imageToDelete, index) => {
    if (imageToDelete.isExisting) {
      const newPhotosArray = images.filter((_, i) => i !== index && _.isExisting);
      const { response, error } = await makeApiRequests({
        requestBody: { photos: newPhotosArray.map(img => ({ url: img.filename, uploadedAt: img.uploadedAt })) },
        endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventory._id),
        method: 'PUT'
      });

      if (error) {
        return toast.error('Failed to delete image: ' + error);
      }

      updateInventoryState(response);
      onInventoryEdit && onInventoryEdit(response, true);

      try {
        await makeApiRequests({
          endpoint: ENDPOINTS.DELETE_DISK_IMAGE(imageToDelete.filename),
          method: 'DELETE'
        });
      } catch (error) {
        console.error('Error deleting file from disk:', error);
      }

      const updatedImages = images.filter((_, i) => i !== index);
      setImages(updatedImages);
    } else {
      const updatedImages = images.filter((_, i) => i !== index);
      setImages(updatedImages);
    }
  };

  const onExistingImageDelete = async (image, index) => {
    const newPhotosArray = inventory.photos.filter((_, i) => i !== index);

    const { response, error } = await makeApiRequests({
      requestBody: { photos: newPhotosArray },
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventory._id),
      method: 'PUT'
    });

    if (error) {
      return toast.error(error);
    }

    updateInventoryState(response);
    onInventoryEdit && onInventoryEdit(response, true);

    try {
      await makeApiRequests({
        endpoint: ENDPOINTS.DELETE_DISK_IMAGE(image.url),
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Failed to delete image file:', error);
    }
  };

  const onFileDeleteClick = file => {
    setDeleteFileModalMeta({ file });
  };

  const deleteFile = async () => {
    const { file: toBeDeletedFile } = deleteFileModalMeta;
    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: true });

    const newFileArray = inventory.files.filter(f => f._id !== toBeDeletedFile._id).map(f => f._id);

    const { response, error } = await makeApiRequests({
      requestBody: { files: newFileArray },
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventory._id),
      method: 'PUT'
    });

    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: false });
    if (error) {
      return toast.error(error);
    }

    setDeleteFileModalMeta(null);
    updateInventoryState(response);
    onInventoryEdit && onInventoryEdit(response, true);
    deleteFileFromServer(toBeDeletedFile);
  };

  return (
    <Container fluid className="py-2 px-md-3 mb-5">
      {isFormLoading && <Loader />}
      {!isFormLoading && form && inventory && (
        <>
          <div className="border rounded p-3 mb-2">
            <InventoryQuickField label="Home type" value={inventory.numberOfSections || homeClassification(inventory).sections || ''} displayValue={<strong>{homeTypeLabel(inventory)}</strong>} disabled={!formDisabled || buttonsDisabled} options={[{value:1,label:'Single wide (1 section)'},{value:2,label:'Double wide (2 sections)'},{value:3,label:'Triple wide (3 sections)'}]} onSave={value => saveQuickUnitField('numberOfSections',value)}/>
            <div className={homeClassification(inventory).needsReview ? 'text-danger' : 'text-muted'}>{homeClassification(inventory).basis}</div>
            {priceText(inventory.contract?.salesPrice ?? inventory.salesPrice) && <div>Sales Price: {priceText(inventory.contract?.salesPrice ?? inventory.salesPrice)}</div>}<div className="mt-2">Maximum Sales Price: <InventoryPriceReference unit={inventory} disabled={!formDisabled || buttonsDisabled} onEdit={() => setShowWorksheet(true)}/></div>
            {!formDisabled && <div className="small">Save or cancel unit edits before opening the worksheet.</div>}
          </div>
          {showWorksheet && <CalculationWorksheetSidebar visible inventory={inventory} appChoices={appChoices} onClose={() => setShowWorksheet(false)} onInventoryUpdate={unit => { updateInventoryState(unit); if (onInventoryQuickEdit) onInventoryQuickEdit(unit); }} />}
          <Row>
            <Col className="text-md-right mt-2 mt-md-0">
              <Button onClick={() => setShowFileViewer(true)} variant="outline-dark" size="sm" className="py-1 ml-2">
                <Files className="align-text-top mr-2" />
                View Files
              </Button>

              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setFormDisabled(false)}
                className=" py-1 ml-2"
                disabled={!formDisabled || buttonsDisabled}
              >
                <Pen className="align-text-top mr-2" />
                {formDisabled ? 'Edit' : 'Edit Mode Active'}
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => setShowDeleteModal(true)}
                className="py-1 ml-2"
                disabled={inventoryDeleting || buttonsDisabled || !deletable}
              >
                <Trash className="align-text-top mr-2" />
                Delete
              </Button>

              {DeleteModal()}
            </Col>
          </Row>
          <hr />

          <FormGenerator
            ref={formRef}
            form={form}
            formValues={formValues}
            onFormValuesChange={handleFormValuesChange}
            onSubmit={onInventoryEditFormSubmit}
            formDisabled={formDisabled}
          />
          {!isFormLoading && (
            <>
              <div className=" text-right">
                <h6
                  className="d-inline-block hover-underline midFont text-primary my-2"
                  onClick={() => setIsCalculationWorkSheetVisible(v => !v)}
                >
                  {isCalculationWorkSheetVisible ? (
                    <>
                      Hide Calculation Work Sheet Fields <ArrowUpCircle size={13} />
                    </>
                  ) : (
                    <>
                      Show Calculation Work Sheet Fields <ArrowDownCircle size={13} />
                    </>
                  )}
                </h6>
              </div>

              {isCalculationWorkSheetVisible && (
                <>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Table bordered size="sm" className="smallFont w-100">
                        <thead>
                          <tr className="bg-primary text-white smallFont">
                            <th colSpan={2} className="text-center">
                              Deletions (from Manufacturer&apos;s Invoice)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {INVENTORY_DELETION_ROWS.map(([label, key]) => (
                            <tr key={key}>
                              <td className="align-middle">{label}</td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={formValues[key] ?? ''}
                                  onChange={handleWorksheetFieldChange(key)}
                                  disabled={formDisabled}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Table bordered size="sm" className="smallFont w-100">
                        <thead>
                          <tr className="bg-primary text-white smallFont">
                            <th colSpan={2} className="text-center">
                              Adds
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="align-middle">Factory Freight</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.factoryFreight'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.factoryFreight')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Air Conditioner</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.airConditioner'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.airConditioner')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Tax From Invoice</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.taxFromInvoice'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.taxFromInvoice')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">HUD Dues/Fees</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.hudDuesFees'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.hudDuesFees')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Delivery &amp; Set</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.deliveryAndSet'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.deliveryAndSet')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Skirting Type</td>
                            <td>
                              <select
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.skirtingType'] ?? ''}
                                onChange={e => {
                                  const type = e.target.value;
                                  const isSingleWide =
                                    Number(formValues.numberOfSections || inventory?.numberOfSections || 1) === 1;
                                  const allowance = getSkirtingAllowanceByType(type, isSingleWide);
                                  setFormValues(prev => ({
                                    ...prev,
                                    'maxSalesPriceCalculatorAddFields.skirtingType': type,
                                    'maxSalesPriceCalculatorAddFields.skirting':
                                      allowance != null
                                        ? String(allowance)
                                        : prev['maxSalesPriceCalculatorAddFields.skirting'] ?? ''
                                  }));
                                }}
                                disabled={formDisabled}
                              >
                                <option value="">— Select —</option>
                                {SKIRTING_TYPE_OPTIONS.map(opt => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Skirting: </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.skirting'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.skirting')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">
                              Steps/Decks: (# of sets{' '}
                              <input
                                type="number"
                                className="form-control form-control-sm d-inline-block"
                                style={{ width: '70px' }}
                                value={formValues['maxSalesPriceCalculatorAddFields.numberOfSets'] ?? ''}
                                onChange={e => {
                                  const val = e.target.value;
                                  const sets = parseInt(val, 10) || 0;
                                  setFormValues(prev => ({
                                    ...prev,
                                    'maxSalesPriceCalculatorAddFields.numberOfSets': val,
                                    'maxSalesPriceCalculatorAddFields.stepsDecks': String(defaultStepsAmount(sets))
                                  }));
                                }}
                                disabled={formDisabled}
                              />{' '}
                              )
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.stepsDecks'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.stepsDecks')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Dirt Pad</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.dirtPad'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.dirtPad')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Footers</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.footers'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.footers')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Trim Out/Tape &amp; Texture</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.trimOutTapeAndTexture'] ?? ''}
                                onChange={handleWorksheetFieldChange(
                                  'maxSalesPriceCalculatorAddFields.trimOutTapeAndTexture'
                                )}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Electric Hookup</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.electricHookup'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.electricHookup')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Water Hookup</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.waterHookup'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.waterHookup')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Sewer Hookup</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.sewerHookup'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.sewerHookup')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Gas Hookup</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.gasHookup'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.gasHookup')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Gutters &amp; Downspouts</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.guttersAndDownspouts'] ?? ''}
                                onChange={handleWorksheetFieldChange(
                                  'maxSalesPriceCalculatorAddFields.guttersAndDownspouts'
                                )}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Other (Desc)</td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.other'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.other')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td className="align-middle">Other Amount</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues['maxSalesPriceCalculatorAddFields.otherAmount'] ?? ''}
                                onChange={handleWorksheetFieldChange('maxSalesPriceCalculatorAddFields.otherAmount')}
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                      <Table bordered size="sm" className="smallFont mt-3 w-100">
                        <tbody>
                          <tr>
                            <td className="align-middle">Sales Tax (not on Invoice)</td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={formValues.maxSalesPriceSalesTax ?? ''}
                                onChange={e =>
                                  setFormValues(prev => ({
                                    ...prev,
                                    maxSalesPriceSalesTax: e.target.value
                                  }))
                                }
                                disabled={formDisabled}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                  <Table bordered size="sm" className="smallFont mt-3">
                    <thead>
                      <tr className="bg-primary text-white smallFont">
                        <th colSpan={4} className="text-center">
                          Land Improvements
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={2} className="align-middle">
                          Land Purchase Price / Payoff
                        </td>
                        <td colSpan={2}>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={formValues.maxSalesPriceLandPurchasePrice ?? ''}
                            onChange={e =>
                              setFormValues(prev => ({
                                ...prev,
                                maxSalesPriceLandPurchasePrice: e.target.value
                              }))
                            }
                            disabled={formDisabled}
                          />
                        </td>
                      </tr>
                      {[
                        [
                          'Basement',
                          'maxSalesPriceLandImprovements.basement',
                          'Grading',
                          'maxSalesPriceLandImprovements.grading'
                        ],
                        [
                          'Brick/Block Skirting',
                          'maxSalesPriceLandImprovements.brickBlockSkirting',
                          'Permanent Foundation',
                          'maxSalesPriceLandImprovements.permanentFoundation'
                        ],
                        [
                          'Carport',
                          'maxSalesPriceLandImprovements.carport',
                          'Permits',
                          'maxSalesPriceLandImprovements.permits'
                        ],
                        ['Deck', 'maxSalesPriceLandImprovements.deck', 'Porch', 'maxSalesPriceLandImprovements.porch'],
                        [
                          'Driveway',
                          'maxSalesPriceLandImprovements.driveway',
                          'Septic',
                          'maxSalesPriceLandImprovements.septic'
                        ],
                        [
                          'Electrical Hookup',
                          'maxSalesPriceLandImprovements.electricalHookup',
                          'Sewer Hookup',
                          'maxSalesPriceLandImprovements.sewerHookup'
                        ],
                        [
                          'Footers',
                          'maxSalesPriceLandImprovements.footers',
                          'Water Hookup',
                          'maxSalesPriceLandImprovements.waterHookup'
                        ],
                        [
                          'Garage',
                          'maxSalesPriceLandImprovements.garage',
                          'Well',
                          'maxSalesPriceLandImprovements.well'
                        ],
                        [
                          'Gas Hookup',
                          'maxSalesPriceLandImprovements.gasHookup',
                          'Other',
                          'maxSalesPriceLandImprovements.other'
                        ]
                      ].map(([labelLeft, keyLeft, labelRight, keyRight]) => (
                        <tr key={keyLeft}>
                          <td className="align-middle">{labelLeft}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={formValues[keyLeft] ?? ''}
                              onChange={e =>
                                setFormValues(prev => ({
                                  ...prev,
                                  [keyLeft]: e.target.value
                                }))
                              }
                              disabled={formDisabled}
                            />
                          </td>
                          <td className="align-middle">{labelRight}</td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={formValues[keyRight] ?? ''}
                              onChange={e =>
                                setFormValues(prev => ({
                                  ...prev,
                                  [keyRight]: e.target.value
                                }))
                              }
                              disabled={formDisabled}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
          {!formDisabled && <File uploadedFiles={uploadedFiles} onUploadedFilesChange={setUploadedFiles} />}
          <Image
            images={images}
            onImagesChange={newImages => {
              setImages(newImages);
            }}
            isEditMode={!formDisabled}
            containerClassName="mt-3"
          />
          {!formDisabled && (
            <SubmitButtonRow disabled={buttonsDisabled} onSaveClick={handleSubmitClick} showGoToDocHub={false} bottom />
          )}

          <Comments
            comments={inventory['comments']}
            disabled={buttonsDisabled}
            onCommentSubmit={submitComment}
            commentText={commentText}
            onCommentTextChange={setCommentText}
            onCommentDeleteClick={onCommentDeleteClick}
          />
          <FileViewer
            sidebarZIndex={2000}
            show={showFileViewer}
            onHide={() => setShowFileViewer(false)}
            files={inventory['files']}
            driveFolder={inventory['driveFolder']}
            onFileDeleteClick={onFileDeleteClick}
          >
            {formDisabled ? <div className="mb-3">
              <h6>Unit details</h6>
              {['bedRooms', 'bathRooms', 'livingRooms'].map((field, index) => <div key={field}>
                {['Bedrooms', 'Bathrooms', 'Living Rooms'][index]}: <InventoryQuickField label={['Bedrooms', 'Bathrooms', 'Living Rooms'][index]} value={field === 'livingRooms' ? inventory.additionalFields?.livingRooms : inventory[field]} onSave={value => saveQuickUnitField(field, value)}/>
              </div>)}
              <div className="mt-2">Google Drive folder: <InventoryQuickField label="Google Drive folder link or ID" link value={inventory.driveFolder} onSave={value => saveQuickUnitField('driveFolder', value)}/></div>
            </div> : <p className="small">Save or cancel your unit edits before changing details here.</p>}
          </FileViewer>

          <AlertModal
            show={deleteFileModalMeta !== null}
            alertText={`Are you sure to delete this file (${
              deleteFileModalMeta
                ? deleteFileModalMeta.file.metadata && deleteFileModalMeta.file.metadata.originalName
                  ? deleteFileModalMeta.file.metadata.originalName
                  : deleteFileModalMeta.file.filename
                : ''
            }). You will lose access to this file permanently?`}
            showProgress={deleteFileModalMeta && deleteFileModalMeta.deletingFile}
            progressText="Deleting file..."
            continueButtonText="Delete"
            onDismissClick={() => setDeleteFileModalMeta(null)}
            onHide={() => setDeleteFileModalMeta(null)}
            onContinueClick={deleteFile}
          />
          {isSuperAdmin() && (
            <AlertModal
              show={deleteCommentModalMeta !== null}
              alertText="Are you sure to remove this comment?"
              showProgress={deleteCommentModalMeta?.showProgress}
              progressText="Deleting comment..."
              onDismissClick={() => setDeleteCommentModalMeta(null)}
              onHide={() => setDeleteCommentModalMeta(null)}
              onContinueClick={deleteComment}
            />
          )}
          <AddEditModal
            existingModels={(appChoices.find(choice => choice.key === 'model')?.values || []).filter(model => String(model.manufacturer?._id || model.manufacturer) === String(modelModalMeta.defaultValues?.manufacturer || formValues.manufacturer))}
        onSelectExisting={model => {
          setFormValues(current => ({ ...current, model: model._id }));
          onModelChange(model._id);
          setModelMetaState({ show: false, loading: false, defaultValues: null, disclaimer: '' });
        }}
        show={modelModalMeta.show}
            onHide={() =>
              setModelMetaState({
                show: false,
                loading: false,
                defaultValues: null,
                disclaimer: ''
              })
            }
            toBeEditedModel={null}
            showProgress={modelModalMeta.loading}
            addForm={getForm({ rows: modelAddRows, type: 'Add' })}
            updateForm={getForm({ rows: modelUpdateRows, type: 'Update' })}
            defaultValues={modelModalMeta.defaultValues}
            disclaimer={modelModalMeta.disclaimer}
          />
        </>
      )}
    </Container>
  );
};

export default EditInventory;
